import { router, useFocusEffect } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, View } from "react-native";
import type {
  AdIntegrationKind,
  AdSourceType,
  DRMConfiguration,
  PlayerError,
} from "react-native-theoplayer";
import { Hide, mq } from "react-native-unistyles";

import Image from "$src/core/components/atoms/Image/Image";
import Text from "$src/core/components/atoms/Text/Text";
import PlayerView from "$src/core/components/organisms/Player";
import { IMAGE_BLUR_DIMENSIONS } from "$src/core/constants/image";
import type { PlayerInstance } from "$src/core/contexts/player";
import useAnalytics from "$src/core/hooks/use-analytics";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import useTranslation from "$src/core/hooks/use-translation";
import useTvConnectHostPlayerController from "$src/core/hooks/use-tv-connect-host-player-controller";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import useEpisodeDetailQuery from "$src/core/queries/use-episode-content-query";
import useLinearContentQuery from "$src/core/queries/use-linear-content-query";
import useMovieDetailQuery from "$src/core/queries/use-movie-content-query";
import useSeriesDetailQuery from "$src/core/queries/use-series-content-query";
import Logging from "$src/core/services/logging/logging";
import ScreenOrientation from "$src/core/services/screen-orientation";
import pickImageUrl from "$src/core/utils/image/pick-image-url";
import mapPlaybackThumbnailTracks from "$src/core/utils/player/map-playback-thumbnail-tracks";

import $, { IMAGE_DIMENSIONS } from "./PlayerPage.styles";

// A source that never reaches loadedmetadata is unplayable; bail out before the
// player exhausts the heap loading chunks it can never render (KAHI-68).
const PLAYBACK_START_TIMEOUT_MS = 30_000;

type Props = {
  id: string;
  type: string;
  trailer?: boolean;
};

const PlayerPage = ({ id, type, trailer }: Props) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [player, setPlayer] = useState<PlayerInstance>();
  const [playerError, setPlayerError] = useState<PlayerError | null>(null);

  const playerDurationRef = useRef(0);

  $.useVariants({ isFullscreen });

  const { data: movieData, status: movieStatus } = useMovieDetailQuery(id, {
    isEnabled: type === "movies",
  });
  const { data: seriesData, status: seriesStatus } = useSeriesDetailQuery(id, {
    isEnabled: type === "series",
  });
  const { data: episodeData, status: episodeStatus } = useEpisodeDetailQuery(
    id,
    { isEnabled: type === "episodes" },
  );
  const { data: linearData, status: linearStatus } = useLinearContentQuery(id, {
    isEnabled: type === "linear",
  });

  const { data, status } = useMemo(() => {
    if (type === "movies") {
      return { data: movieData, status: movieStatus };
    }

    if (type === "linear") {
      return { data: linearData, status: linearStatus };
    }

    if (type === "series") {
      return { data: seriesData, status: seriesStatus };
    }

    return { data: episodeData, status: episodeStatus };
  }, [
    movieData,
    movieStatus,
    seriesData,
    seriesStatus,
    episodeData,
    episodeStatus,
    linearData,
    linearStatus,
    type,
  ]);

  // Play the main item; fall back to the trailer when there is no main
  // playback (or when a trailer was explicitly requested).
  const isPlayingTrailer = !!data?.trailer && (!!trailer || !data.playback);

  const sources = useMemo(() => {
    if (isPlayingTrailer && data.trailer) {
      return data.trailer.sources.map((source) => ({
        src: source.url,
        type: source.type,
        contentProtection: source.drm as DRMConfiguration,
      }));
    }

    if (data?.playback) {
      return data.playback.sources.map((source) => ({
        src: source.url,
        type: source.type,
        contentProtection: source.drm as DRMConfiguration,
      }));
    }

    return undefined;
  }, [data, isPlayingTrailer]);

  const thumbnailTracks = useMemo(() => {
    const playback =
      isPlayingTrailer && data.trailer ? data.trailer : data?.playback;
    return mapPlaybackThumbnailTracks(playback?.tracks);
  }, [data, isPlayingTrailer]);

  const ads = useMemo(() => {
    if (data?.playback?.ads) {
      // Convert timeOffset to proper format if needed
      const { timeOffset } = data.playback.ads;
      const formattedTimeOffset =
        timeOffset && timeOffset !== "0" ? timeOffset : "start";

      // Ensure the ad URL has proper protocol
      const adUrl = data.playback.ads.url.startsWith("http")
        ? data.playback.ads.url
        : `https://${data.playback.ads.url}`;

      const adConfig = {
        integration: "google-ima" as AdIntegrationKind,
        timeOffset: formattedTimeOffset,
        sources: {
          src: adUrl,
          type: (data.playback.ads.type === "VAST"
            ? "vast"
            : "vmap") as AdSourceType,
        },
      };

      // For VMAP, use as-is (VMAP should work with proper IMA setup)
      if (data.playback.ads.type === "VMAP") {
        // VMAP ads typically don't need timeOffset as they define their own timing
        const { timeOffset: unusedTimeOffset, ...vmapConfig } = adConfig;
        return [vmapConfig];
      }

      return [adConfig];
    }

    return undefined;
  }, [data]);

  const poster = useMemo(() => {
    const image = data?.image ?? data?.images;
    return {
      uri: image ? pickImageUrl(image, ["portrait", "landscape"]) : undefined,
      dimensions: IMAGE_DIMENSIONS.poster,
    };
  }, [data?.image, data?.images]);

  const handlePlayerReady = async (newPlayer: PlayerInstance) => {
    setPlayer(newPlayer);
  };

  const placeholderPosterImage = useResponsiveImage(poster.uri, {
    dimensions: [IMAGE_BLUR_DIMENSIONS],
  });
  const responsivePosterImage = useResponsiveImage(poster.uri, {
    dimensions: poster.dimensions,
  });

  const handleEnterFullscreen = async () => {
    if (!player) return;
    setIsFullscreen(true);
  };

  const handleExitFullscreen = async () => {
    if (!player) return;
    setIsFullscreen(false);
  };

  useTvConnectHostPlayerController({
    player,
    contentId: id,
    contentType: type,
    contentTitle: data?.title,
    contentImageUrl: poster.uri,
    trailer: isPlayingTrailer,
  });

  useFocusEffect(
    useCallback(() => {
      if (!player) return undefined;

      const handleDurationChange = () => {
        playerDurationRef.current = player.duration;
      };

      const handleError = (event: any) => {
        setPlayerError(event?.error as PlayerError | null);
      };

      player.addEventListener("durationchange", handleDurationChange);
      player.addEventListener("error", handleError);

      if (data?.playback) {
        trackEvent("content_interaction", {
          interaction_type: "start",
          content_title: data.title,
          content_type:
            data.playback.sources[0].type === "live" ? "live" : "vod",
          content_duration: playerDurationRef.current,
        });
      }

      if (sources?.length) {
        player.play();
      }
      return () => {
        player.pause();

        player.removeEventListener("durationchange", handleDurationChange);
        player.removeEventListener("error", handleError);
      };
    }, [player, data, sources, trackEvent]),
  );

  /* Fail fast when a source never finishes preparing — otherwise the player keeps loading chunks until the heap is exhausted (KAHI-68). */
  useEffect(() => {
    if (!player || !sources?.length || playerError) return undefined;

    const timeout = setTimeout(() => {
      const error = {
        errorCode: "SOURCE_NOT_PLAYABLE",
        errorMessage: `Playback did not start within ${PLAYBACK_START_TIMEOUT_MS}ms`,
      } as unknown as PlayerError;

      Logging.error(
        new Error(
          `Player preparation timed out for ${type}/${id}: ${sources[0]?.src}`,
        ),
      );
      setPlayerError(error);
    }, PLAYBACK_START_TIMEOUT_MS);

    // Only an advancing position proves the source prepared. "durationchange"
    // fires from the manifest and "playing" fires optimistically on play(), so
    // an unplayable stream emits both while rendering nothing.
    const handleProgress = () => {
      if (player.currentTime > 0) clearTimeout(timeout);
    };

    player.addEventListener("timeupdate", handleProgress);

    return () => {
      clearTimeout(timeout);
      player.removeEventListener("timeupdate", handleProgress);
    };
  }, [player, sources, playerError, type, id]);

  useEffect(() => {
    void ScreenOrientation?.lockAsync("PORTRAIT_UP");
    return () => {
      void ScreenOrientation?.unlockAsync();
    };
  }, []);

  const title = data?.title ?? data?.subtitle ?? "";
  const subtitle = data?.subtitle ?? data?.tags?.join(" · ") ?? "";

  if (status === "pending") {
    return <LoadingPage />;
  }

  if (!data?.playback && !data?.trailer) {
    return (
      <ErrorPage
        title={t("ERROR/PLAYER_PAGE/TITLE")}
        subtitle={t("ERROR/PLAYER_PAGE/SUBTITLE")}
        ctaText={t("ERROR/NOT_FOUND/CTA")}
        ctaIcon="home"
        onPressCta={() => router.navigate("/storefront")}
      />
    );
  }

  return !!sources?.length && !playerError ? (
    <View style={$.container}>
      <View style={$.player}>
        <PlayerView
          title={data.title}
          subtitle={data.shortDescription}
          sources={sources}
          ads={ads}
          thumbnailTracks={thumbnailTracks}
          onPlayerReady={handlePlayerReady}
          onPlayerDestroy={() => setPlayer(undefined)}
          onEnterFullscreen={handleEnterFullscreen}
          onExitFullscreen={handleExitFullscreen}
          videoType={data.playback?.isLive ? "live" : "vod"}
        />
      </View>

      {!Platform.isTV && !isFullscreen && (
        <Hide mq={mq.only.width("lg")}>
          <View style={$.content}>
            <View style={$.meta}>
              {/* This is a decorative image, so we don't need an accessibility label */}
              {/* eslint-disable-next-line triple-rn-a11y/accessibility-label */}
              <Image
                accessibilityRole="none"
                placeholder={placeholderPosterImage}
                placeholderContentFit="cover"
                source={responsivePosterImage}
                contentFit="cover"
                style={$.poster}
              />
              <View style={$.metaDetails}>
                <Text
                  accessibilityRole="header"
                  preset="heading1"
                  style={$.title}
                  numberOfLines={2}
                >
                  {title}
                </Text>
                <Text accessibilityRole="text" preset="span" numberOfLines={1}>
                  {subtitle}
                </Text>
              </View>
            </View>
            <Text accessibilityRole="text" preset="body" style={$.description}>
              {data.shortDescription}
            </Text>
          </View>
        </Hide>
      )}
    </View>
  ) : (
    <ErrorPage
      title={t("ERROR/PLAYER_PAGE/TITLE")}
      subtitle={t("ERROR/PLAYER_PAGE/SUBTITLE")}
      ctaText={t("ERROR/NOT_FOUND/CTA")}
      ctaIcon="home"
      onPressCta={() => router.navigate("/storefront")}
      error={playerError}
    />
  );
};

export default PlayerPage;
